import { useEffect, useState } from "react";
import { Plus, Package, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = ["Device", "Subscription", "HDMI Cable", "Remote Control", "Accessory", "Other"];

const empty = {
  name: "", category: "Device", quantity: 0,
  cost_price: 0, selling_price: 0, supplier: "", low_stock_level: 5,
};

function statusBadge(status) {
  if (status === "Out of Stock") return "bg-destructive text-white";
  if (status === "Low Stock") return "bg-gold text-white";
  return "bg-emerald-100 text-emerald-700";
}

export default function Inventory() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, quantity: p.quantity,
      cost_price: p.cost_price ?? 0, selling_price: p.selling_price,
      supplier: p.supplier || "", low_stock_level: p.low_stock_level,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        cost_price: Number(form.cost_price),
        selling_price: Number(form.selling_price),
        low_stock_level: Number(form.low_stock_level),
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = products.filter((p) => {
    if (filter === "all") return true;
    if (filter === "low") return p.status === "Low Stock";
    if (filter === "out") return p.status === "Out of Stock";
    return p.category === filter;
  });

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy">Inventory</h1>
          <p className="text-sm text-muted-foreground">Track stock for devices, subscriptions and accessories.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="inventory-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              <SelectItem value="low">Low stock</SelectItem>
              <SelectItem value="out">Out of stock</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="add-product-btn" onClick={openCreate} className="bg-navy hover:bg-navy-hover text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Stock
          </Button>
        </div>
      </div>

      <Card className="section-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Low @</th>
                {isAdmin && <th className="text-right px-4 py-3">Cost</th>}
                <th className="text-right px-4 py-3">Sell</th>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={isAdmin ? 9 : 8} className="px-4 py-10 text-center text-muted-foreground">No products found.</td></tr>
              ) : filtered.map((p) => (
                <tr key={p.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`product-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-navy flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-muted-foreground" /> {p.name}
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className="font-normal">{p.category}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold">{p.quantity}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{p.low_stock_level}</td>
                  {isAdmin && <td className="px-4 py-3 text-right text-muted-foreground">${(p.cost_price ?? 0).toFixed(2)}</td>}
                  <td className="px-4 py-3 text-right font-semibold">${p.selling_price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{p.supplier || "—"}</td>
                  <td className="px-4 py-3"><Badge className={statusBadge(p.status)}>{p.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Product name *</Label>
              <Input required data-testid="prod-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="prod-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input type="number" min="0" data-testid="prod-qty-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>Cost price (CAD)</Label>
                <Input type="number" step="0.01" min="0" data-testid="prod-cost-input" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Selling price (CAD) *</Label>
              <Input required type="number" step="0.01" min="0" data-testid="prod-price-input" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Low stock warning at</Label>
              <Input type="number" min="0" data-testid="prod-low-input" value={form.low_stock_level} onChange={(e) => setForm({ ...form, low_stock_level: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Supplier</Label>
              <Input data-testid="prod-supplier-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-product-btn" className="bg-navy hover:bg-navy-hover text-white">
                {editingId ? "Save changes" : "Add product"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
